import { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";

const SHAPE_COLORS = [
  { chip: "bg-blue-500 text-white", cell: "bg-blue-500", ring: "ring-blue-500" },
  { chip: "bg-emerald-500 text-white", cell: "bg-emerald-500", ring: "ring-emerald-500" },
  { chip: "bg-orange-500 text-white", cell: "bg-orange-500", ring: "ring-orange-500" },
  { chip: "bg-violet-500 text-white", cell: "bg-violet-500", ring: "ring-violet-500" },
];

export interface GridDrawProps {
  cols: number;
  rows: number;
  unitLabel: string;
  shapes: Array<{ id: string; label: string }>;
  value: Record<string, number[]>;
  onChange?: (v: Record<string, number[]>) => void;
  disabled?: boolean;
  verdict?: Record<string, boolean> | null;
}

/** Click or drag to shade squares for the selected shape. Each square counts as one unit of area. */
export function GridDraw({ cols, rows, unitLabel, shapes, value, onChange, disabled, verdict }: GridDrawProps) {
  const [active, setActive] = useState(shapes[0]?.id);
  const [mode, setMode] = useState<"paint" | "erase">("paint");
  const dragging = useRef<null | "paint" | "erase">(null);
  const owner = new Map<number, string>();
  for (const s of shapes) for (const c of value[s.id] || []) owner.set(c, s.id);

  useEffect(() => {
    const up = () => { dragging.current = null; };
    window.addEventListener("mouseup", up); window.addEventListener("touchend", up);
    return () => { window.removeEventListener("mouseup", up); window.removeEventListener("touchend", up); };
  }, []);

  const apply = (cell: number, action: "paint" | "erase") => {
    if (disabled || !onChange || !active) return;
    const next: Record<string, number[]> = {};
    for (const s of shapes) next[s.id] = (value[s.id] || []).filter(c => c !== cell);
    if (action === "paint") next[active] = [...next[active], cell].sort((a, b) => a - b);
    onChange(next);
  };
  const start = (cell: number) => {
    if (disabled) return;
    const action: "paint" | "erase" = mode === "erase" || owner.get(cell) === active ? "erase" : "paint";
    dragging.current = action;
    apply(cell, action);
  };
  const enter = (cell: number) => { if (dragging.current) apply(cell, dragging.current); };

  const cellSize = cols > 16 ? 18 : cols > 12 ? 22 : 28;
  return (
    <div className="space-y-3 select-none">
      <div className="flex flex-wrap items-center gap-2">
        {shapes.map((s, i) => {
          const col = SHAPE_COLORS[i % SHAPE_COLORS.length];
          const area = (value[s.id] || []).length;
          const v = verdict?.[s.id];
          return (
            <button key={s.id} type="button" disabled={disabled} onClick={() => { setActive(s.id); setMode("paint"); }}
              className={`rounded-full px-3 py-1 text-sm font-semibold flex items-center gap-2 ${col.chip} ${active === s.id && mode === "paint" ? `ring-2 ring-offset-2 ${col.ring}` : "opacity-80"} disabled:opacity-70`}
              data-testid={`grid-shape-${s.id}`}>
              {s.label} <span className="bg-white/25 rounded-full px-1.5 tabular-nums text-xs">{area} sq</span>
              {v !== undefined && v !== null && <span className="text-xs">{v ? "✓" : "✗"}</span>}
            </button>
          );
        })}
        {!disabled && (
          <button type="button" onClick={() => setMode(m => (m === "erase" ? "paint" : "erase"))}
            className={`rounded-full px-3 py-1 text-sm font-semibold flex items-center gap-1 border-2 ${mode === "erase" ? "bg-foreground text-background border-foreground" : "bg-card border-border"}`} data-testid="grid-erase">
            <Eraser className="w-3.5 h-3.5" /> Erase
          </button>
        )}
      </div>
      <div className="inline-block rounded-xl border border-border bg-card p-2 overflow-x-auto max-w-full">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`, touchAction: disabled ? "auto" : "none" }} onMouseLeave={() => { dragging.current = null; }}>
          {Array.from({ length: cols * rows }, (_, cell) => {
            const o = owner.get(cell);
            const idx = o ? shapes.findIndex(s => s.id === o) : -1;
            const col = idx >= 0 ? SHAPE_COLORS[idx % SHAPE_COLORS.length].cell : "";
            return (
              <div key={cell} role="button" aria-label={`square ${cell + 1}`}
                onMouseDown={e => { e.preventDefault(); start(cell); }} onMouseEnter={() => enter(cell)}
                onTouchStart={() => start(cell)}
                onTouchMove={e => {
                  const t = e.touches[0]; const el = document.elementFromPoint(t.clientX, t.clientY) as HTMLElement | null;
                  const c = el?.dataset?.cell; if (c !== undefined) enter(Number(c));
                }}
                data-cell={cell}
                className={`border border-border/60 ${col || "bg-background hover:bg-muted"} ${disabled ? "cursor-default" : "cursor-crosshair"}`}
                style={{ width: cellSize, height: cellSize }} />
            );
          })}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{unitLabel}. Grid is {cols} squares across and {rows} squares down.</p>
    </div>
  );
}
